import React from 'react';

interface LeadScoreCardProps {
  score: number | null;
  grade?: string;
  trend?: 'up' | 'down' | 'stable';
  lastCalculated?: string;
  breakdown?: Array<{
    rule: string;
    points: number;
    matched: boolean;
  }>;
  onClick?: () => void;
}

const LeadScoreCard: React.FC<LeadScoreCardProps> = ({
  score,
  grade,
  trend,
  lastCalculated,
  breakdown,
  onClick
}) => {
  const displayScore = score != null ? Math.round(score) : 0;
  const displayGrade = grade || calculateGrade(displayScore);
  
  const gradeColors = {
    'A+': 'bg-emerald-500 text-white',
    'A': 'bg-emerald-400 text-white',
    'A-': 'bg-emerald-300 text-white',
    'B+': 'bg-blue-500 text-white',
    'B': 'bg-blue-400 text-white',
    'B-': 'bg-blue-300 text-white',
    'C+': 'bg-amber-500 text-white',
    'C': 'bg-amber-400 text-white',
    'C-': 'bg-amber-300 text-white',
    'D': 'bg-orange-400 text-white',
    'F': 'bg-red-400 text-white'
  };

  const scoreColor = displayScore >= 80 ? 'text-emerald-600' 
                   : displayScore >= 60 ? 'text-blue-600'
                   : displayScore >= 40 ? 'text-amber-600'
                   : 'text-gray-600';

  return (
    <div 
      className={`rounded-2xl bg-gradient-to-br from-white to-gray-50 p-6 shadow-sm ring-1 ring-gray-200 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-sm font-medium text-gray-600 mb-1">Lead Score</h3>
          <div className="flex items-baseline gap-2">
            <span className={`text-4xl font-bold ${scoreColor}`}>{displayScore}</span>
            <span className="text-sm text-gray-500">/ 100</span>
          </div>
        </div>
        
        <div className="flex flex-col items-end gap-2">
          <span className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-sm font-bold ${gradeColors[displayGrade as keyof typeof gradeColors]}`}>
            {displayGrade}
          </span>
          
          {trend && (
            <div className="flex items-center gap-1 text-xs">
              {trend === 'up' && (
                <>
                  <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  <span className="text-emerald-600 font-medium">Trending up</span>
                </>
              )}
              {trend === 'down' && (
                <>
                  <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                  </svg>
                  <span className="text-red-600 font-medium">Trending down</span>
                </>
              )}
              {trend === 'stable' && (
                <>
                  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
                  </svg>
                  <span className="text-gray-600 font-medium">Stable</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${
              displayScore >= 80 ? 'bg-emerald-500' 
              : displayScore >= 60 ? 'bg-blue-500'
              : displayScore >= 40 ? 'bg-amber-500'
              : 'bg-gray-400'
            }`}
            style={{ width: `${displayScore}%` }}
          />
        </div>
      </div>

      {/* Score Breakdown */}
      {breakdown && breakdown.length > 0 && (
        <div className="space-y-2 mb-4">
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Contributing Factors</h4>
          <div className="space-y-1">
            {breakdown.slice(0, 3).map((item, index) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  {item.matched ? (
                    <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  )}
                  <span className={item.matched ? 'text-gray-700' : 'text-gray-400'}>{item.rule}</span>
                </div>
                <span className={`font-semibold ${item.matched ? 'text-emerald-600' : 'text-gray-400'}`}>
                  {item.matched ? '+' : ''}{item.points}
                </span>
              </div>
            ))}
            {breakdown.length > 3 && (
              <button className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                View all {breakdown.length} factors →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Last Calculated */}
      {lastCalculated && (
        <div className="flex items-center gap-2 text-xs text-gray-500 pt-3 border-t border-gray-100">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Last calculated {lastCalculated}</span>
        </div>
      )}

      {/* Recalculate Button */}
      <button 
        className="mt-3 w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          // Handle recalculation
        }}
      >
        Recalculate Score
      </button>
    </div>
  );
};

function calculateGrade(score: number): string {
  if (score >= 97) return 'A+';
  if (score >= 93) return 'A';
  if (score >= 90) return 'A-';
  if (score >= 87) return 'B+';
  if (score >= 83) return 'B';
  if (score >= 80) return 'B-';
  if (score >= 77) return 'C+';
  if (score >= 73) return 'C';
  if (score >= 70) return 'C-';
  if (score >= 60) return 'D';
  return 'F';
}

export default LeadScoreCard;
